import { Autocomplete, Option, Select, Typography } from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useBlocker, useNavigate } from '@tanstack/react-router';
import { Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetTracingContactContactQueryKey,
  useGetTracingContactContactSuspense,
  useGetTracingContactSuspense,
  useUpdateTracingContactContact,
} from '../../../../../api/generated/contact-tracing-controller/contact-tracing-controller.ts';
import { getGetContactPatientContactPlacesQueryOptions } from '../../../../../api/generated/contact-tracing-place-controller/contact-tracing-place-controller.ts';
import {
  AddressContactDto,
  AddressDto,
  AddressDtoAddressType,
  ContactTracingContactDto,
  ContactTracingContactDtoResidencyType,
  ContactTracingPlaceDto,
  PatientAddressDto,
  PatientAddressDtoReferenceType,
} from '../../../../../api/generated/model';
import {
  getGetPatientAddressesQueryKey,
  getGetPatientByIdQueryKey,
} from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';
import { InstitutionContactFields } from '../../../../../components/form/InstitutionContactFields.tsx';
import { SingeLineFormGrid, SingleLineFormControl } from '../../../../../components/form/SingeLineFormComponents.tsx';
import { LeavePageWarning } from '../../../../../components/LeavePageWarning.tsx';
import { FieldMetaInfos, useAutoSaveForm } from '../../../../../hooks/useAutoSaveForm.ts';
import { getCurrentDate } from '../../../../../utils/date.ts';
import { getResidencyTypeName } from '../../../../../utils/residencyType.ts';
import { SelectContactPatientContactPlace } from './SelectContactPatientContactPlace.tsx';

export function EditTracingContactContactForm({ tracingContactId }: { tracingContactId: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: tracingContactContact } = useGetTracingContactContactSuspense(tracingContactId);
  const { data: tracingContactDetails } = useGetTracingContactSuspense(tracingContactId);

  const updateTracingContactContactMutation = useUpdateTracingContactContact({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(getGetTracingContactContactQueryKey(tracingContactId), data);
        void queryClient.resetQueries(getGetContactPatientContactPlacesQueryOptions());
        void queryClient.invalidateQueries({ queryKey: getGetPatientByIdQueryKey(tracingContactId) });
        void queryClient.invalidateQueries({ queryKey: getGetPatientAddressesQueryKey(tracingContactId) });
      },
      onError: () =>
        toast.error('Beim dem speichern der Kontaktdaten ist ein Fehler aufgetreten', { toastId: 'saveError' }),
    },
  });

  const {
    control,
    getFieldState,
    watch,
    setValue,
    formState: { isFormDirty },
    scheduleSave,
  } = useAutoSaveForm({
    sourceData: tracingContactContact,
    getDefaultValues,
    save: (data) =>
      updateTracingContactContactMutation.mutateAsync({
        id: tracingContactId,
        data: {
          contactPlace: data.contactPlace ?? undefined,
          contactPatientExposure: data.contactPatientExposure ? data.contactPatientExposure : undefined,
          contactPatientLastContact: data.contactPatientLastContact,
          residencyType: data.residencyType ?? undefined,
          address: data.address
            ? ({
                address: data.address,
                contact: data.contact ?? undefined,
                from: getCurrentDate(),
                patientId: tracingContactId,
                referenceType: PatientAddressDtoReferenceType.PATIENT_RESIDENCE,
              } as PatientAddressDto)
            : undefined,
          doctor: data.doctorAddress
            ? ({
                address: data.doctorAddress,
                contact: data.doctorContact ?? undefined,
                from: getCurrentDate(),
                patientId: tracingContactId,
                referenceType: PatientAddressDtoReferenceType.PATIENT_GENERAL_DOCTOR,
              } as PatientAddressDto)
            : undefined,
          referencePerson: tracingContactContact.referencePerson ?? undefined,
        },
      }),
    fieldMetaInfos,
    validationMode: 'onChange',
    onValidationFailed: () =>
      toast.warn('Kontaktdaten können aufgrund eines Fehlers momentan nicht gespeichert werden', {
        toastId: 'validationError',
        autoClose: false,
      }),
    onValidationPassed: () => toast.dismiss('validationError'),
  });

  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty && !updateTracingContactContactMutation.isPending,
    withResolver: true,
  });

  return (
    <>
      <SingeLineFormGrid>
        {tracingContactDetails.contactPatientCaseNumber && (
          <SingleLineFormControl
            label={'Fallnummer'}
            fieldState={{ isDirty: false, invalid: false, isTouched: false, isValidating: false, error: undefined }}>
            <Typography
              sx={{ py: 0.75, cursor: 'pointer' }}
              onClick={() =>
                navigate({ to: '/patients/$patientId', params: { patientId: tracingContactDetails.contactPatientId } })
              }>
              {tracingContactDetails.contactPatientCaseNumber}
            </Typography>
          </SingleLineFormControl>
        )}
        <SingleLineFormControl label={'Kontakt Ort'} fieldState={getFieldState('contactPlace')}>
          <Controller
            name={'contactPlace'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <SelectContactPatientContactPlace onChange={onChange} value={value} variant={'plain'} />
            )}
          />
        </SingleLineFormControl>
        <SingleLineFormControl label={'Kontaktdauer (h)'} fieldState={getFieldState('contactPatientExposure')}>
          <Controller
            name={'contactPatientExposure'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                freeSolo
                value={value ?? ''}
                inputValue={value?.toString() ?? ''}
                onChange={(_, value) => onChange(value)}
                onInputChange={(_, value) => onChange(value)}
                options={['>8 Stunden', '>40 Stunden']}
                variant={'plain'}
              />
            )}
          />
        </SingleLineFormControl>
        <SingleLineFormControl label={'Letzter Kontakt'} fieldState={getFieldState('contactPatientLastContact')}>
          <GermanDateInput
            control={control}
            name="contactPatientLastContact"
            rules={{
              required: false,
            }}
            onValueBlur={() => scheduleSave()}
          />
        </SingleLineFormControl>
        <SingleLineFormControl label={'Wohnhaft'} fieldState={getFieldState('residencyType')}>
          <Controller
            name={'residencyType'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select onChange={(_, value) => onChange(value)} value={value} variant={'plain'}>
                {Object.values(ContactTracingContactDtoResidencyType).map((residence) => (
                  <Option value={residence} key={residence}>
                    {getResidencyTypeName(residence)}
                  </Option>
                ))}
              </Select>
            )}
          />
        </SingleLineFormControl>
        <InstitutionContactFields
          formMethods={{ control, watch, setValue, getFieldState }}
          layout="singleLine"
          addressField="address"
          contactField="contact"
          addressLabel="Adresse"
          contactLabel="Ansprechperson"
          createOptionLabel="Neue Ansprechsperson erstellen"
          addressTypes={[AddressDtoAddressType.ACCOMMODATION_PRIVATE, AddressDtoAddressType.ACCOMMODATION_ASYLUM]}
          patientId={tracingContactId}
          canChangeAddressType={true}
          variant="plain"
          clearContactOnAddressChange="always"
          contactClearOptions={false}
          useAllContactsList={true}
        />
        <InstitutionContactFields
          formMethods={{ control, watch, setValue, getFieldState }}
          layout="singleLine"
          addressField="doctorAddress"
          contactField="doctorContact"
          addressLabel="Hausarzt"
          contactLabel="Hausarzt Kontakt"
          addressTypes={[AddressDtoAddressType.ACCOMMODATION_PRIVATE, AddressDtoAddressType.ACCOMMODATION_ASYLUM]}
          canChangeAddressType={false}
          variant="plain"
          clearContactOnAddressChange="always"
          contactClearOptions={false}
          useAllContactsList={true}
        />
      </SingeLineFormGrid>
      <LeavePageWarning
        open={blocker.status === 'blocked'}
        onDelete={() => blocker.proceed}
        onCancel={() => blocker.reset}
        onSave={() => {
          if (blocker.reset) {
            blocker.reset();
          }
          scheduleSave();
        }}
        title={`Kontaktdaten behalten?`}
      />
    </>
  );
}

interface FormValues {
  contactPlace: ContactTracingPlaceDto | null;
  contactPatientExposure: string | null;
  contactPatientLastContact: string;
  residencyType: ContactTracingContactDtoResidencyType | null;
  address: AddressDto | null;
  contact: AddressContactDto | null;
  doctorAddress: AddressDto | null;
  doctorContact: AddressContactDto | null;
}

function getDefaultValues(tracingContact: ContactTracingContactDto) {
  return {
    contactPlace: tracingContact.contactPlace ?? null,
    contactPatientExposure: tracingContact.contactPatientExposure ?? null,
    contactPatientLastContact: tracingContact.contactPatientLastContact ?? '',
    residencyType: tracingContact.residencyType ?? null,
    address: tracingContact.address?.address ?? null,
    contact: tracingContact.address?.contact ?? null,
    doctorAddress: tracingContact.doctor?.address ?? null,
    doctorContact: tracingContact.doctor?.contact ?? null,
  };
}

const fieldMetaInfos: FieldMetaInfos<FormValues> = {
  contactPlace: { debounce: false, ignorePropertiesForComparison: ['id'] },
  contactPatientExposure: { debounce: false },
  contactPatientLastContact: { debounce: true },
  residencyType: { debounce: false },
  address: { debounce: true, ignorePropertiesForComparison: ['id'] },
  contact: { debounce: true, ignorePropertiesForComparison: ['id', 'addressId'] },
  doctorAddress: { debounce: true, ignorePropertiesForComparison: ['id'] },
  doctorContact: { debounce: true, ignorePropertiesForComparison: ['id', 'addressId'] },
};
